import { useTranslate } from '@refinedev/core';
import type { CrudFilter } from '@refinedev/core';
import { List } from '@refinedev/antd';
import { Table, Input, Form, Row, Col, Tag } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { WithdrawalContractOut } from '@/api/generated/schemas';
import { useTableSearch } from '@/hooks/useTableSearch';
import { useAdminTable } from '@/hooks/useAdminTable';
import { TextFilter } from '@/components/common/TextFilter';
import { ShowLink } from '@/components/common/ShowLink';
import { RevenueCatCustomerLink } from '@/components/monetization/RevenueCatLink';
import { WaiverStatusTag } from '@/components/monetization/withdrawal_contracts/WaiverStatusTag';
import { isFieldFiltered } from '@/utils/filters';
import { getSortOrder } from '@/utils/sorters';

const RESOURCE = 'monetization.withdrawal_contracts';

export const WithdrawalContractList = () => {
  const t = useTranslate();
  const { onSearch, createDebouncedSubmit } = useTableSearch();

  const { tableProps, searchFormProps, sorters, setFilters, filters } =
    useAdminTable<WithdrawalContractOut>({
      pagination: { pageSize: 20 },
      resource: RESOURCE,
      onSearch,
      sorters: {
        initial: [{ field: 'id', order: 'desc' }],
      },
    });

  const debouncedSubmit = createDebouncedSubmit(searchFormProps.form);

  return (
    <List>
      <Form {...searchFormProps} layout="vertical">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={16} md={12} lg={8}>
            <Form.Item name="q" style={{ marginBottom: 0 }}>
              <Input
                placeholder={t(
                  'monetization.withdrawal_contracts.search.placeholder'
                )}
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => debouncedSubmit(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Table
        {...tableProps}
        pagination={{ ...tableProps.pagination, simple: true }}
        rowKey="id"
      >
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
          render={(value: number) => (
            <ShowLink resource={RESOURCE} id={value} />
          )}
        />

        <Table.Column
          dataIndex="waiver_id"
          title={t('monetization.withdrawal_contracts.fields.waiver_status')}
          sorter
          sortOrder={getSortOrder(sorters, 'waiver_id')}
          render={(_: unknown, record: WithdrawalContractOut) => (
            <WaiverStatusTag
              waiverId={record.waiver_id}
              fingerprintValid={record.consent_fingerprint_valid}
              waivedAt={record.waived_at}
            />
          )}
        />

        <Table.Column
          dataIndex="event_type"
          title={t('monetization.withdrawal_contracts.fields.event_type')}
          sorter
          sortOrder={getSortOrder(sorters, 'event_type')}
          render={(value: string | null) => (value ? <Tag>{value}</Tag> : '-')}
        />

        <Table.Column
          dataIndex="account_id"
          title={t('monetization.withdrawal_contracts.fields.account_id')}
          sorter
          sortOrder={getSortOrder(sorters, 'account_id')}
          render={(value: number | null) => value ?? '-'}
          filtered={isFieldFiltered(filters as CrudFilter[], 'account_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <TextFilter
              field="account_id"
              width={200}
              placeholder={t(
                'monetization.withdrawal_contracts.filter.account_id'
              )}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="app_user_id"
          title={t('monetization.withdrawal_contracts.fields.app_user_id')}
          render={(value: string | null) =>
            value ? (
              <RevenueCatCustomerLink
                id={value}
                title={t('monetization.customers.actions.open_in_revenuecat')}
                stopRowClick
              />
            ) : (
              '-'
            )
          }
        />

        <Table.Column
          dataIndex="store"
          title={t('monetization.withdrawal_contracts.fields.store')}
          sorter
          sortOrder={getSortOrder(sorters, 'store')}
          render={(value: string | null) => (value ? <Tag>{value}</Tag> : '-')}
        />

        <Table.Column
          dataIndex="product_id"
          title={t('monetization.withdrawal_contracts.fields.product_id')}
          sorter
          sortOrder={getSortOrder(sorters, 'product_id')}
          render={(value: string | null, record: WithdrawalContractOut) => (
            <>
              {value ?? '-'}
              {record.new_product_id && record.new_product_id !== value && (
                <> → {record.new_product_id}</>
              )}
            </>
          )}
        />

        <Table.Column
          dataIndex="consent_version"
          title={t('monetization.withdrawal_contracts.fields.consent_version')}
          render={(value: string | null) => (value ? <Tag>{value}</Tag> : '-')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'consent_version')}
          filterDropdown={({ confirm, clearFilters }) => (
            <TextFilter
              field="consent_version"
              width={200}
              placeholder={t(
                'monetization.withdrawal_contracts.filter.consent_version'
              )}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="event_timestamp"
          title={t('monetization.withdrawal_contracts.fields.event_timestamp')}
          sorter
          sortOrder={getSortOrder(sorters, 'event_timestamp')}
          render={(value: string | null) =>
            value ? new Date(value).toLocaleString() : '-'
          }
        />

        <Table.Column
          dataIndex="waived_at"
          title={t('monetization.withdrawal_contracts.fields.waived_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'waived_at')}
          render={(value: string | null) =>
            value ? new Date(value).toLocaleString() : '-'
          }
        />

        <Table.Column
          dataIndex="matched_at"
          title={t('monetization.withdrawal_contracts.fields.matched_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'matched_at')}
          render={(value: string | null) =>
            value ? new Date(value).toLocaleString() : '-'
          }
        />
      </Table>
    </List>
  );
};
